import React, { useImperativeHandle, useRef, forwardRef, useEffect } from 'react';
import day from 'dayjs';
import _ from 'lodash';
import moment from 'moment';
import { useMemoizedFn } from 'ahooks';
import TableForm, { FormColumn } from '#components/TableForm';
import { Button, Empty, Space } from '@bedrock/components';
import { Plus, NoticeTextLeft } from '@bedrock/icons-react';
import { SFormItemProps } from '#components/SForm/SFormItem';
import NoFiles from '@bedrock/components/es/Empty/images/small/NoFiles'
import { calculateTimeConsuming } from '#features/utils';
import OvertimeTag from '#features/OvertimeTag';
import {ImportFileType, useImportFileModal} from "#features/importFileModal";
import { financial } from 'utils/number';
import { useFilterValues, FilterForm, useColumns } from './useFilterValues';

import "./index.less"

type Props = {
    value?: any[];
    onChange?: (value: any[]) => void;
    className?: string;
    view?: boolean;
}

const Index = (props: Props, ref) => {
    const { className, view = false } = props
    const tableFormRef = useRef(null)
    const { filterValues, showValue, originalShowValue, setState, addRow, deleteRow, onFinish, handleReset, optionsMap } = useFilterValues(props);
    const { fakeKey, createdAt } = useColumns(view);

    useImperativeHandle(ref, () => {
        return {
            validateFields: () => {
                return tableFormRef.current?.form?.validateFields()
                    .catch((error) => {
                        const errMessage = error?.errorFields?.[0]?.errors?.[0]
                        return Promise.reject(errMessage)
                    })
            },
            form: tableFormRef.current?.form
        }
    }, [])

    const columns: FormColumn[] = [
        fakeKey,
        // 作品链接 - 固定在左侧，400px
        {
            title: '作品链接',
            dataIndex: 'work_url',
            width: '280px' ,
            required: true,
            align: 'left',
            fixed: 'left',
            formItem: (text, record, index) => {
                const options = optionsMap?.['kol_platform']?.options || [];
                const platformItem = options.find(item => item?.value === originalShowValue[index]?.platform);
                return {
                    formItemKey: 'work_url',
                    className: 'view-input-work_url',
                    componentClass: 'textareapopover',
                    view: view || record?.id,
                    required: typeof platformItem?.need_work_url === 'boolean' ? platformItem.need_work_url : true,
                    elementProps: {
                        size: 'small',
                        ableCopy: true,
                        textShowTitle: text || '--',
                        onClick: () => { view ? window.open(text, '_blank') : null },
                        style: { cursor: 'pointer', color: '#007AFF' },
                        defaultChildrenProps: { size: 'small' },
                    },
                }
            },
        },
        // 平台 - 120px
        {
            title: '平台',
            dataIndex: 'platform',
            width: '120px',
            required: true,
            align: 'left',
            formItem: (text, record, index) => {
                return {
                    formItemKey: 'platform',
                    componentClass: 'select',
                    view: view || record?.id,
                    elementProps: {
                        size: 'small',
                        style: { width: '120px' },
                        options: optionsMap?.['kol_platform']?.options || [],
                        dropdownStyle: {
                            width: '200px',
                        }
                    }
                } as SFormItemProps
            }
        },
        // 作者ID - 160px
        {
            title: '作者ID',
            dataIndex: 'kol_code',
            width: '160px',
            required: true,
            align: 'left',
            formItem: (text, record, index) => {
                return {
                    formItemKey: 'kol_code',
                    className: 'view-input-work_url',
                    componentClass: 'input',
                    view: view || record?.id,
                    elementProps: {
                        size: 'small',
                        ableCopy: true,
                        tipToolTitle: '复制',
                    }
                } as SFormItemProps
            }
        },
        // 昵称 - 160px
        {
            title: '昵称',
            dataIndex: 'kol_name',
            width: view ? '120px' : '160px',
            required: true,
            align: 'left',
            formItem: (text, record, index) => {
                return {
                    formItemKey: 'kol_name',
                    className: 'view-input-work_url',
                    componentClass: 'input',
                    view: view || record?.id,
                    elementProps: {
                        size: 'small'
                    }
                } as SFormItemProps
            }
        },
        // 加热费用 - 264px
        {
            title: '加热费用',
            dataIndex: 'boost_cost',
            width: view ? '200px' : '264px',
            required: true,
            align: 'right',
            formItem: (text, record, index) => {
                return {
                    className: `view-input-right ${!record?.id ? 'view-amount' : ''}`,
                    formItemKey: 'boost_cost',
                    componentClass: 'currencyType',
                    view: view || record?.id,
                    elementProps: {
                        size: 'small',
                        selectStyle: { width: '80px' },
                        selectDisabled: true,
                        currencyColor: 'black',
                    },
                } as SFormItemProps
            },
        },
        // 加热效果播放量 - 160px
        {
            title: '加热效果播放量',
            dataIndex: 'boost_view_count',
            width: view ? '132px' : '160px',
            required: true,
            align: 'right',
            formItem: (text, record, index) => {
                return {
                    className: 'view-input-right',
                    formItemKey: 'boost_view_count',
                    componentClass: 'inputNumber',
                    view: view || record?.id,
                    elementProps: {
                        className: 'fw-bold',
                        size: 'small',
                        showComponent: (value) => {
                            return <div className='fw-bold'>{financial(value, true)}</div>
                        },
                    },
                } as SFormItemProps
            }
        },
        // 加热开始时间 - 200px
        {
            title: '加热开始时间',
            dataIndex: 'boost_start_time',
            width: view ? '160px' : '200px',
            required: true,
            align: 'left',
            formItem: (text, record, index) => {
                const newState = [...originalShowValue]
                const endTime = newState?.[index]?.boost_end_time
                let days;
                if (endTime) {
                    days = day(endTime).format('YYYY-MM-DD')
                }
                return {
                    formItemKey: 'boost_start_time',
                    className: 'view-date-picker',
                    componentClass: 'datepicker',
                    view: view || record?.id,
                    elementProps: {
                        onChange: (date) => {
                            newState[index].boost_start_time = date
                            if (!date) {
                                newState[index].boost_end_time = null
                                setState(newState)
                            }
                        },
                        showNow: true,
                        disabledDate: (current) => {
                            if (!days) {
                                return current && current > day(days);
                            }
                            return current && day(current).valueOf() > day(days).valueOf()
                        },
                        showTime: { format: 'HH:mm' },
                        format: "YYYY-MM-DD HH:mm",
                        size: 'small'
                    },
                } as SFormItemProps
            }
        },
        // 加热结束时间 - 200px
        {
            title: '加热结束时间',
            dataIndex: 'boost_end_time',
            width: view ? '160px' : '200px',
            required: true,
            align: 'left',
            formItem: (text, record, index) => {
                const newState = [...originalShowValue]
                const startTime = newState?.[index]?.boost_start_time
                let days;
                if (startTime) {
                    days = day(startTime).format('YYYY-MM-DD')
                }
                return {
                    formItemKey: 'boost_end_time',
                    className: 'view-date-picker',
                    componentClass: 'datepicker',
                    view: view || record?.id,
                    elementProps: {
                        onChange: (date) => {
                            newState[index].boost_end_time = date
                            if (!date) {
                                newState[index].boost_start_time = null
                                setState(newState)
                            }
                        },
                        disabledDate: (current) => {
                            if (!days) {
                                return current && current > day(days);
                            }
                            return current && (day(current).valueOf() < day(days).valueOf() || current > day())
                        },
                        showTime: { format: 'HH:mm' },
                        format: "YYYY-MM-DD HH:mm",
                        size: 'small'
                    },
                } as SFormItemProps
            }
        },
        // 加热链接上传时间
        createdAt,
        // 加热链接上传用时 - 160px，固定在右侧
        {
            title: '加热链接上传用时',
            dataIndex: 'boost_upload_time',
            width: '160px',
            align: 'left',
            fixed: 'right',
            render: (text, record, index) => {
                if (!record?.id) {
                    return '--'
                }
                // 上传时间
                const created_at = moment(record?.created_at).valueOf()
                // 加热结束时间
                const boost_end_time = moment(record?.boost_end_time).valueOf()
                const time = created_at - boost_end_time
                const value = calculateTimeConsuming(time);
                return (
                    <div className='leading-none'>
                        <div style={{ lineHeight: '22px' }}>{value}</div>
                        <div className='m-t-4'>
                            <OvertimeTag 
                                delay_grade={record?.delay_grade} 
                                delay_label={record.delay_label} 
                                className="m-l-0" 
                                tagCompColorLight 
                            />
                        </div>
                    </div>
                )
            }
        },
        // 操作 - 52px，固定在右侧
        {
            title: '操作',
            dataIndex: 'operation',
            className: 'operation-cell',
            width: '56px',
            align: 'left',
            fixed: 'right',
            render: (text, record, index) => {
                return <Button 
                    disabled={showValue?.length === 1}
                    type="link" danger onClick={() => { deleteRow(record) }} >删除</Button>
            }
        },
    ].filter(Boolean) as any

    const handleAddBoost = useMemoizedFn(() => {
        addRow();
    })

    useEffect(() => {
        if (showValue.length > 0) {
            tableFormRef?.current?.innerTableRef?.current?.scrollTo({
                top: 264 * (showValue.length -1)
            })
        }
    }, [showValue?.length]);
    
    const uploadFinishedCallback = useMemoizedFn((res) => {
        if (res?.data?.data?.length && res?.data?.fail_count === 0) {
            const newState = [...originalShowValue]
            res.data.data?.forEach?.((item, index) => {
                newState.push({
                    ...item,
                    boost_cost: {
                        value: item?.boost_cost,
                    },
                    boost_start_time: item?.boost_start_time ? day(item?.boost_start_time).format('YYYY-MM-DD HH:mm') : '',
                    boost_end_time: item?.boost_end_time ? day(item?.boost_end_time).format('YYYY-MM-DD HH:mm') : '',
                    key: Date.now() + '' + index,
                })
            })
            setState(newState)
            closeImportFileModal()
        }
    })

    const {openImportFileModal, closeImportFileModal} = useImportFileModal({
        importType: ImportFileType.boost_effect,
        importIsMemory: false,
        uploadFinishedCallback: uploadFinishedCallback,
    })

    return (
        <div className={className}>
            <FilterForm onFinish={onFinish} handleReset={handleReset} filterValues={filterValues} optionsMap={optionsMap} />
            <TableForm
                ref={tableFormRef}
                scroll={{ x: 'max-content', y: 'calc(100vh - 274px)' }}
                className='boost-table-form'
                emptyText={<Empty imageNode={<NoFiles />} text="暂无数据" />}
                value={showValue}
                rowKey={(record) => record?.id}
                onChange={setState}
                formColumns={columns}
                pagination={false}
            />
            <div className='m-t-12'>
                <Space size={12}>
                    <Button className='boost-table-container-add-button' size='medium' onClick={handleAddBoost} type='checked' icon={<Plus />}>新增</Button>
                    <Button className='boost-table-container-add-button' size='medium' onClick={() => openImportFileModal({})} type='checked' icon={<NoticeTextLeft />}>批量导入</Button>
                </Space>
            </div>
        </div>
    )
}

export default forwardRef(Index)           